import { useState } from "react"
import { Button } from "react-bootstrap"
import { ConfirmModal } from "../../Modals/confirmModal"
import { useAppDispatch } from "../../../redux/store"
import { showSuccess, showError } from "../../../redux/counter/counterSlice"
import { useSendBookingConfirmedEmailMutation, useSendBookingConfirmedEmailToTargetMutation, useSendRegistrationReminderEmailMutation, useSendRegistrationReminderEmailToTargetMutation } from "../../../services/bClientApi"
import AccessClickButton from "../../Buttons/AccessClickButton"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import canAccess from "../../../services/accessService/accessService"

interface BookingEmailActionsProps {
    bookingData: PropertyBookingEntry
    emailAddress: string
    myEmail?: string
}

export default function BookingEmailActions({ bookingData, emailAddress, myEmail }: BookingEmailActionsProps) {
    const bookingId = bookingData._id || 'xxx'
    const dispatch = useAppDispatch()
    const [sendBookingEmailModal, setSendBookingEmailModal] = useState(false)
    const [sendBookingConfirmedEmailToMyself, setSendBookingConfirmedEmailToMyself] = useState(false)
    const [sendRegistrationReminderModal, setSendRegistrationReminderModal] = useState(false)
    const [sendRegistrationReminderToMyself, setSendRegistrationReminderToMyself] = useState(false)

    const [
        sendBookingConfirmedEmail,
        {
            isLoading: sendBookingConfirmedEmailLoading,
        }
    ] = useSendBookingConfirmedEmailMutation()

    const [
        sendBookingConfirmedEmailToTarget,
        {
            isLoading: sendBookingConfirmedEmailToTargetLoading,
        }
    ] = useSendBookingConfirmedEmailToTargetMutation()

    const [
        sendRegistrationReminderEmail,
        {
            isLoading: sendRegistrationReminderEmailLoading,
        }
    ] = useSendRegistrationReminderEmailMutation()

    const [
        sendRegistrationReminderEmailToTarget,
        {
            isLoading: sendRegistrationReminderEmailToTargetLoading,
        }
    ] = useSendRegistrationReminderEmailToTargetMutation()

    const sendEmail = async () => {
        const isToMyself = sendBookingConfirmedEmailToMyself
        setSendBookingEmailModal(false)
        setSendBookingConfirmedEmailToMyself(false)
        try {
            if (isToMyself) {
                await sendBookingConfirmedEmailToTarget({
                    bookingId: bookingId,
                    emailTarget: myEmail || '',
                }).unwrap()
                dispatch(showSuccess('Email Sent'))
            } else {
                await sendBookingConfirmedEmail(bookingId).unwrap()
                dispatch(showSuccess('Email Sent'))
            }
        } catch (error) {
            console.error('Error sending email:', error)
            dispatch(showError('Failed to send email'))
        }
    }

    const sendRegistrationReminderEmailHandler = async () => {
        const isToMyself = sendRegistrationReminderToMyself
        setSendRegistrationReminderModal(false)
        setSendRegistrationReminderToMyself(false)
        try {
            if (isToMyself) {
                await sendRegistrationReminderEmailToTarget({
                    bookingId: bookingId,
                    emailTarget: myEmail || '',
                }).unwrap()
                dispatch(showSuccess('Registration Reminder Email Sent'))
            } else {
                await sendRegistrationReminderEmail(bookingId).unwrap()
                dispatch(showSuccess('Registration Reminder Email Sent'))
            }
        } catch (error) {
            console.error('Error sending registration reminder email:', error)
            dispatch(showError('Failed to send registration reminder email'))
        }
    }

    if (!canAccess('booking', 'email-actions', { payload: bookingData })) {
        return null
    }

    const disableButtons = sendBookingConfirmedEmailLoading || sendBookingConfirmedEmailToTargetLoading || sendRegistrationReminderEmailLoading || sendRegistrationReminderEmailToTargetLoading

    return (
        <>
            <h3>Booking Email Functions</h3>
            {emailAddress && (
                <>
                    <b>Email Address:</b> {emailAddress}<br />
                    <Button
                        className={"mb-2"}
                        variant="primary"
                        disabled={disableButtons}
                        onClick={async () => {
                            setSendBookingEmailModal(true)
                        }}
                    >
                        Send Booking Confirmation Email
                    </Button>
                    <br />
                    <Button
                        className={"mb-2"}
                        variant="warning"
                        disabled={disableButtons}
                        onClick={async () => {
                            setSendRegistrationReminderModal(true)
                        }}
                    >
                        Send Registration Reminder Email
                    </Button>
                    <br />
                </>
            )}

            {!emailAddress && (
                <>
                    <b>Booking does not have an email address.</b>
                    <br />
                </>
            )}

            <AccessClickButton
                resource={'booking'}
                action={'email-actions'}
                size="sm"
                className={"mb-2"}
                variant="secondary"
                disabled={disableButtons}
                onClick={async () => {
                    setSendBookingConfirmedEmailToMyself(true)
                }}
                text={"Send Confirmation To Myself"}
            />
            {" "}
            <AccessClickButton
                resource={'booking'}
                action={'email-actions'}
                size="sm"
                className={"mb-2"}
                variant="secondary"
                disabled={disableButtons}
                onClick={async () => {
                    setSendRegistrationReminderToMyself(true)
                }}
                text={"Send Registration Reminder To Myself"}
            />

            <ConfirmModal
                title="Send Booking Confirmation Email"
                message="Are you sure you want to send the confirmation email?"
                active={sendBookingEmailModal || sendBookingConfirmedEmailToMyself}
                onClose={() => {
                    setSendBookingEmailModal(false)
                    setSendBookingConfirmedEmailToMyself(false)
                }}
                onConfirm={sendEmail}
            />

            <ConfirmModal
                title="Send Registration Reminder Email"
                message="Are you sure you want to send the registration reminder email?"
                active={sendRegistrationReminderModal || sendRegistrationReminderToMyself}
                onClose={() => {
                    setSendRegistrationReminderModal(false)
                    setSendRegistrationReminderToMyself(false)
                }}
                onConfirm={sendRegistrationReminderEmailHandler}
            />
        </>
    )
}
